import { useEffect, useState } from 'react'
import { useLiveQuery } from '@tanstack/react-db'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDb } from '@/lib/dbContext'

/**
 * The master WhatsApp sending switch (app_settings.whatsapp_sending_enabled),
 * with a confirm dialog. Shown on both the WhatsApp page's Configure tab and
 * the admin Settings page — it is the one control staff reach for in a hurry,
 * so it lives wherever they look for it.
 */
export function WhatsAppSendingSwitch() {
  const db = useDb()
  const settingsResult = useLiveQuery((q) =>
    q.from({ settings: db.appSettingsCollection }),
  )

  useEffect(() => {
    void db.appSettingsCollection.preload?.()
  }, [db])

  const settings = settingsResult.data?.[0]
  const enabled = settings?.whatsappSendingEnabled ?? false

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const target = !enabled // the state we'd switch to

  const applyToggle = async () => {
    setSaving(true)
    setError(null)
    try {
      await db.setWhatsAppSending(target)
      setConfirmOpen(false)
    } catch {
      setError('Could not change the setting. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-xl space-y-1">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-slate-900">
                Message sending
              </h2>
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${
                  enabled
                    ? 'bg-teal-100 text-teal-700'
                    : 'bg-slate-100 text-slate-500'
                }`}
              >
                {enabled ? 'On' : 'Off'}
              </span>
            </div>
            <p className="text-[13px] text-slate-500">
              When on, queued messages are dispatched to customers via Twilio
              once their cancel window has passed. When off, messages stay
              queued and nothing is sent.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setError(null)
              setConfirmOpen(true)
            }}
            className={`inline-flex h-9 items-center rounded-lg px-4 text-[13px] font-semibold ${
              enabled
                ? 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                : 'bg-slate-900 text-white hover:bg-slate-800'
            }`}
          >
            {enabled ? 'Turn off sending' : 'Turn on sending'}
          </button>
        </div>
      </section>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!saving) setConfirmOpen(open)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {target ? 'Turn on WhatsApp sending?' : 'Turn off WhatsApp sending?'}
            </DialogTitle>
            <DialogDescription>
              {target
                ? 'Queued messages past their cancel window will start being sent to customers via Twilio.'
                : 'Messages will stay queued and won’t be sent until you turn sending back on.'}
            </DialogDescription>
          </DialogHeader>
          {error ? (
            <p className="text-[13px] text-rose-600">{error}</p>
          ) : null}
          <DialogFooter>
            <button
              type="button"
              disabled={saving}
              onClick={() => setConfirmOpen(false)}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => void applyToggle()}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60 ${
                target
                  ? 'bg-teal-600 hover:bg-teal-700'
                  : 'bg-slate-900 hover:bg-slate-800'
              }`}
            >
              {saving ? (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : null}
              {target ? 'Turn on sending' : 'Turn off sending'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
